import React, { useEffect } from "react";
import {
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  AlertColor,
  Alert,
  Snackbar,
  Radio,
  FormControlLabel,
  RadioGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  Toolbar,
} from "@mui/material";
import { SocialDoorData } from "./DoorSettingsPage";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import { useState } from "react";
import { SciOptionalBehaviorType } from "./DoorSettingsPage";

type BasicSettingsProps = {
  doorData: SocialDoorData | undefined;
};

type SignageContents = {
  autoSciUserTypeId: number;
  contentIdentifier: string;
}[];

type UserTypes = {
  sciUserTypes: {
    autoSciUserTypeId: number;
    sciUserTypeName: string;
  }[];
  total: number;
};

type OptionalBehaviorType = SciOptionalBehaviorType | undefined;

const BasicSettings: React.FC<BasicSettingsProps> = ({ doorData }) => {
  // ラジオボタンで扱う変数
  const [guideBell, setGuideBell] = useState<OptionalBehaviorType>(undefined);
  const [guideBellOpen, setGuideBellOpen] =
    useState<OptionalBehaviorType>(undefined);
  const [proximityOpen, setProximityOpen] =
    useState<OptionalBehaviorType>(undefined);
  const [speaker, setSpeaker] = useState<boolean>(false);
  const [sciHelpOk, setSciHelpOk] = useState<string>("1");
  // サイネージ設定で扱う変数
  const [userTypes, setUserTypes] = useState<UserTypes | undefined>(undefined);
  // const [signageContents, setSignageContents] = useState<SignageContents>([]);

  // アラート
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");

  useEffect(() => {
    if (doorData) {
      setGuideBell(doorData?.sciGuideBellBehavior);
      setGuideBellOpen(doorData?.sciGuideBellOpenBehavior);
      setProximityOpen(doorData?.sciProximityOpenBehavior);
      setSpeaker(doorData?.sciSpeakerBehavior !== "OFF");
      setSciHelpOk(doorData?.sciHelpOk || "1"); // 初期値設定
      // const signageContent = doorData?.sciSignageContents;
      // setSignageContents(signageContent);
    }
  }, [doorData]);

  // API呼び出し
  useEffect(() => {
    const fetchData = async () => {
      try {
        // call indexSciUserTypes API
        const response = await fetch(
          process.env.REACT_APP_API_ENDPOINT + `/sci-user-types`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: await getJwtTokenFromIdToken(),
            },
          }
        );

        const data = await response.json();
        setUserTypes(data);
        console.log("API取得データ" + JSON.stringify(userTypes));
      } catch (error) {
        console.error(error);
      }
    };

    // 非同期関数を実行
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (doorData && doorData.sdcUuid) {
      // const validSignageContents = signageContents.filter((content) => {
      //   return content.contentIdentifier !== "";
      // });
      const settings = {
        sciGuideBellBehavior: guideBell,
        sciGuideBellOpenBehavior: guideBellOpen,
        sciProximityOpenBehavior: proximityOpen,
        sciSpeakerBehavior: speaker ? "ALWAYS_ON" : "OFF",
        // sciSignageContents: validSignageContents,
      };
      console.log("API送信情報" + JSON.stringify(settings));

      try {
        // call setSciBehavior API
        const response = await fetch(
          process.env.REACT_APP_API_ENDPOINT +
            `/sdc/${doorData.sdcUuid}/set-sci-behavior`,
          {
            method: "POST",
            mode: "cors",
            headers: {
              "Content-Type": "application/json",
              Authorization: await getJwtTokenFromIdToken(),
            },
            body: JSON.stringify(settings),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        } else {
          setSnackbarMessage("ドア設定を保存しました");
          setAlertSeverity("success");
          setSnackbarOpen(true);
          //表示を更新する
        }

        console.log("doorData or sdcUuid has changed");
      } catch (e) {
        console.error("Error save door configuration:", e);
        setSnackbarMessage("ドアの保存に失敗しました。");
        setAlertSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };

  const handleRadioButtonChange = (groupName: string, value: string) => {
    console.log(`グループ "${groupName}" で "${value}" を選択しました`);
    let sciOptionalBehavior: SciOptionalBehaviorType;
    switch (value) {
      case "ALWAYS_ON":
        sciOptionalBehavior = value;
        break;
      case "ON_WHEN_REQUESTED":
        sciOptionalBehavior = value;
        break;
      default:
        sciOptionalBehavior = "OFF";
    }
    switch (groupName) {
      case "guideBell":
        setGuideBell(sciOptionalBehavior);
        break;
      case "guideBellOpen":
        setGuideBellOpen(sciOptionalBehavior);
        break;
      case "proximityOpen":
        setProximityOpen(sciOptionalBehavior);
        break;
      default:
    }
  };

  const radioButtonsInfo = [
    {
      name: "guideBellOpen",
      title: "ドア開閉音声",
      options: [
        { label: "常にON", value: "ALWAYS_ON" },
        { label: "アプリ要求でON", value: "ON_WHEN_REQUESTED" },
        { label: "OFF", value: "OFF" },
      ],
      select: guideBellOpen ? guideBellOpen : "",
    },
    {
      name: "proximityOpen",
      title: "ドアに近づいたら開ける",
      options: [
        { label: "アプリ要求でON", value: "ON_WHEN_REQUESTED" },
        { label: "OFF", value: "OFF" },
      ],
      select: proximityOpen ? proximityOpen : "",
    },
  ];

  // const getContentIdentifier = (id: number) => {
  //   const content = signageContents.filter((content) => {
  //     return content.autoSciUserTypeId === id;
  //   });
  //   if (content.length === 0) return "";
  //   else return content[0].contentIdentifier;
  // };

  // const handleSignageChange = (event: any, id: number) => {
  //   const newValue = event.target.value;
  //   console.log(`サイネージ設定 id ${id} で ${newValue} が選択されました。`);

  //   let copiedSignageContents: SignageContents = JSON.parse(
  //     JSON.stringify(signageContents)
  //   );

  //   const filteredSignageContents = signageContents.filter((content) => {
  //     return content.autoSciUserTypeId === id;
  //   });

  //   if (filteredSignageContents.length === 0) {
  //     copiedSignageContents.push({
  //       autoSciUserTypeId: id,
  //       contentIdentifier: newValue,
  //     });
  //     setSignageContents(copiedSignageContents);
  //   } else {
  //     setSignageContents(
  //       copiedSignageContents.map((content) => {
  //         if (content.autoSciUserTypeId === id) {
  //           return {
  //             ...content,
  //             contentIdentifier: newValue,
  //           };
  //         }
  //         return content;
  //       })
  //     );
  //   }
  // };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableContainer component={Paper} style={{ marginBottom: 20 }}>
            <Table>
              <TableBody>
                <TableRow key="guideBell" sx={{ opacity: doorData?.isGuideBellEnabled ? 1 : 0.5 }}>
                    <TableCell>
                      <Typography color={doorData?.isGuideBellEnabled ? "textPrimary" : "textSecondary"}>
                        盲導鈴設定:
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FormControl>
                        <RadioGroup row value={guideBell || ""}>
                          <FormControlLabel
                            control={<Radio disabled={!doorData?.isGuideBellEnabled} />}
                            label="常にON"
                            value="ALWAYS_ON"
                            onChange={() =>
                              handleRadioButtonChange("guideBell", "ALWAYS_ON")
                            }
                          />
                          <FormControlLabel
                            control={<Radio disabled={!doorData?.isGuideBellEnabled} />}
                            label="アプリ要求でON"
                            value="ON_WHEN_REQUESTED"
                            onChange={() =>
                              handleRadioButtonChange(
                                "guideBell",
                                "ON_WHEN_REQUESTED"
                              )
                            }
                          />
                          <FormControlLabel
                            control={<Radio disabled={!doorData?.isGuideBellEnabled} />}
                            label="OFF"
                            value="OFF"
                            onChange={() =>
                              handleRadioButtonChange("guideBell", "OFF")
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                    </TableCell>
                  </TableRow>

                {radioButtonsInfo.map((radioButton) => (
                  <TableRow 
                    key={radioButton.name} 
                    sx={{ 
                      opacity: (radioButton.name === "guideBellOpen" && !doorData?.isGuideBellEnabled) ? 0.5 : 1 
                    }}
                  >
                    <TableCell>
                      <Typography 
                        color={
                          radioButton.name === "guideBellOpen" && !doorData?.isGuideBellEnabled
                            ? "textSecondary" 
                            : "textPrimary"
                        }
                      >
                        {radioButton.title}:
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FormControl>
                        <RadioGroup row value={radioButton.select}>
                          {radioButton.options.map((option: any) => (
                            <FormControlLabel
                              key={option.value}
                              control={
                                <Radio 
                                  disabled={
                                    radioButton.name === "guideBellOpen" && !doorData?.isGuideBellEnabled
                                  }
                                />
                              }
                              label={option.label}
                              value={option.value}
                              onChange={() =>
                                handleRadioButtonChange(
                                  radioButton.name,
                                  option.value
                                )
                              }
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ opacity: doorData?.isSpeakerEnabled ? 1 : 0.5 }}>
                      <TableCell>
                        <Typography color={doorData?.isSpeakerEnabled ? "textPrimary" : "textSecondary"}>
                          介助要請を受け付ける:
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl>
                          <RadioGroup row value={speaker}>
                            <FormControlLabel
                              control={<Radio disabled={!doorData?.isSpeakerEnabled} />}
                              label="YES"
                              value={true}
                              onChange={() => setSpeaker(true)}
                            />
                            <FormControlLabel
                              control={<Radio disabled={!doorData?.isSpeakerEnabled} />}
                              label="NO"
                              value={false}
                              onChange={() => setSpeaker(false)}
                            />
                          </RadioGroup>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                    {/* 介助要請受領でドア音声を鳴らす */}
                    <TableRow sx={{ opacity: (doorData?.isSpeakerEnabled && doorData?.isGuideBellEnabled) ? 1 : 0.5 }}>
                      <TableCell>
                        <Typography color={(doorData?.isSpeakerEnabled && doorData?.isGuideBellEnabled) ? "textPrimary" : "textSecondary"}>
                          介助要請受領でドア音声を鳴らす:
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl>
                          <RadioGroup
                            row
                            value={sciHelpOk}
                            onChange={(e) => setSciHelpOk(e.target.value)}
                          >
                            <FormControlLabel
                              control={<Radio disabled={!doorData?.isSpeakerEnabled || !doorData?.isGuideBellEnabled} />}
                              label="ON"
                              value="1"
                            />
                            <FormControlLabel
                              control={<Radio disabled={!doorData?.isSpeakerEnabled || !doorData?.isGuideBellEnabled} />}
                              label="OFF"
                              value="0"
                            />
                          </RadioGroup>
                        </FormControl>
                      </TableCell>
                    </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {/* <TableContainer component={Paper}>
            <Toolbar>
              <Typography
                sx={{ flex: "1 1 100%" }}
                variant="h6"
                id="tableTitle"
                component="div"
              >
                サイネージ設定
              </Typography>
            </Toolbar>
            <Table>
              <TableBody>
                {userTypes?.sciUserTypes.map((userType) => (
                  <TableRow key={userType.autoSciUserTypeId}>
                    <TableCell>{userType.sciUserTypeName}</TableCell>
                    <TableCell>
                      <FormControl
                        variant="standard"
                        sx={{ m: 1, minWidth: 120 }}
                      >
                        <InputLabel id="sciContent">表示選択</InputLabel>
                        <Select
                          value={getContentIdentifier(
                            userType.autoSciUserTypeId
                          )}
                          onChange={(event) =>
                            handleSignageChange(
                              event,
                              userType.autoSciUserTypeId
                            )
                          }
                          label="contentIdentifier"
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="0">0</MenuItem>
                          <MenuItem value="1">1</MenuItem>
                          <MenuItem value="2">2</MenuItem>
                          <MenuItem value="3">3</MenuItem>
                          <MenuItem value="4">4</MenuItem>
                          <MenuItem value="5">5</MenuItem>
                          <MenuItem value="6">6</MenuItem>
                          <MenuItem value="7">7</MenuItem>
                          <MenuItem value="8">8</MenuItem>
                          <MenuItem value="9">9</MenuItem>
                          <MenuItem value="10">10</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer> */}
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button variant="outlined" color="primary" onClick={handleSave}>
              設定保存
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={alertSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BasicSettings;
